import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  hasPermission,
  type PermissionsMap,
  type RoleKey,
} from "../../config/permissions";

interface CurrentUser {
  email: string;
  name: string;
}

export interface PreviousLogin {
  time: string;
  ip: string | null;
}

interface PermissionsContextValue {
  user: CurrentUser | null;
  permissions: PermissionsMap;
  roles: string[];
  loading: boolean;
  authenticated: boolean;
  previousLogin: PreviousLogin | null;
  can: (required: RoleKey | null | undefined) => boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextValue | undefined>(
  undefined,
);

// Server source-of-truth: GET /api/auth/user-permissions requires a valid
// session in EVERY environment (including LOCAL).  The only way to populate
// this context is to have logged in first.
const PERMISSIONS_ENDPOINT = "/api/auth/user-permissions";

export const PermissionsProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [permissions, setPermissions] = useState<PermissionsMap>({});
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [previousLogin, setPreviousLogin] = useState<PreviousLogin | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // Security note (Checkmarx Missing_HSTS_Header, CWE-346): HSTS is a
      // response-header concern enforced at the serving layer, not in this
      // client fetch. It is already set for every response by the frontend's
      // nginx config (see src/frontend/Dockerfile) and by the API's
      // SecurityHeadersMiddleware (Strict-Transport-Security: max-age=63072000;
      // includeSubDomains). This finding is therefore a false positive.
      const res = await fetch(PERMISSIONS_ENDPOINT, {
        credentials: "include",
        headers: { "Cache-Control": "no-store" },
      });
      if (!res.ok) {
        setUser(null);
        setPermissions({});
        setRoles([]);
        setPreviousLogin(null);
        return;
      }
      const data = await res.json();
      setUser(data.user ?? null);
      setPermissions(data.permissions ?? {});
      setRoles(data.roles ?? []);
      setPreviousLogin(data.previousLogin ?? null);
    } catch {
      setUser(null);
      setPermissions({});
      setRoles([]);
      setPreviousLogin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    // Clear local UI state first so any flash before navigation looks right.
    setUser(null);
    setPermissions({});
    setRoles([]);
    // Full-page navigation to the GET logout endpoint. The server destroys
    // the encrypted session cookie *and* 302s through Entra's
    // /oauth2/v2.0/logout so the IdP-side SSO session is also terminated.
    // Without this hop, returning to /login would silently re-authenticate
    // via the still-valid Entra session and drop the user back into the app.
    window.location.href = "/api/auth/logout";
  }, []);

  const value = useMemo<PermissionsContextValue>(
    () => ({
      user,
      permissions,
      roles,
      loading,
      authenticated: !!user,
      previousLogin,
      can: (required) => hasPermission(permissions, required),
      refresh,
      signOut,
    }),
    [user, permissions, roles, loading, previousLogin, refresh, signOut],
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = (): PermissionsContextValue => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    throw new Error("usePermissions must be used inside <PermissionsProvider>");
  }
  return ctx;
};
